import type { IFormulaRequestCache } from '@utils/managers/IFormulaRequestCache';

type CacheEntry<T = unknown> = {
    promise: Promise<T>;
    settledAt?: number;
};

const normalizeCacheValue = (value: unknown): unknown => {
    if (value instanceof Date) {
        const timestamp = value.getTime();
        return { $date: Number.isNaN(timestamp) ? 'Invalid Date' : value.toISOString() };
    }
    if (Array.isArray(value)) return value.map(normalizeCacheValue);
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([key, item]) => [key, normalizeCacheValue(item)]),
        );
    }
    if (value === undefined) return { $undefined: true };
    if (typeof value === 'number' && !Number.isFinite(value)) return { $number: String(value) };
    return value;
};

export const makeFormulaRequestCacheKey = (operation: string, args: readonly unknown[]): string =>
    `${operation}:${JSON.stringify(args.map(normalizeCacheValue))}`;

/**
 * Дедуплицирует одинаковые in-flight запросы и ненадолго сохраняет успешный результат.
 * Ошибки не кэшируются: следующий validation-run может повторить запрос.
 */
export class FormulaRequestCache implements IFormulaRequestCache {
    private readonly entries = new Map<string, CacheEntry>();

    constructor(private readonly ttlMs = 10_000) {}

    getOrCreate<T>(operation: string, args: readonly unknown[], loader: () => Promise<T>): Promise<T> {
        const key = makeFormulaRequestCacheKey(operation, args);
        const existing = this.entries.get(key) as CacheEntry<T> | undefined;
        if (existing && (existing.settledAt === undefined || Date.now() - existing.settledAt < this.ttlMs)) {
            return existing.promise;
        }

        const entry: CacheEntry<T> = { promise: Promise.resolve().then(loader) };
        this.entries.set(key, entry);
        entry.promise.then(
            () => {
                if (this.entries.get(key) === entry) entry.settledAt = Date.now();
            },
            () => {
                if (this.entries.get(key) === entry) this.entries.delete(key);
            },
        );
        return entry.promise;
    }

    clear(): void {
        this.entries.clear();
    }
}
